import React, { useEffect, useState } from 'react';
import {
  ThemeProvider,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';

import { theme } from '../../Medical_History_Forms/themeprovider';
import { useLoader } from '../../loaderProvider/loader';
import { defaultRetrievalData, getOocyteRetrieval, MasterconfigrutationDetails } from './OocyteRetrival.helper';
import { infoTextStyle, sectionTitleStyle } from '../../../utils/commonUtils';

interface OocyteRetrievalDetailsProps {
  cycleId: number | null;
  dropDownOptions?: MasterconfigrutationDetails;
}

const labelFromEnum = (
  items: { value: number | string; label: string }[],
  value?: number | string
): string | undefined => items.find(item => item.value === value)?.label;

const renderField = (
  label: string,
  value: string | number | undefined | null
) => {
  const isEmpty = value === undefined || value === null || value === '';
  if (isEmpty) return null;

  return (
    <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
      <Typography variant="body1" sx={sectionTitleStyle}>
        {label}
      </Typography>
      <Typography variant="body2" sx={infoTextStyle}>
        {value}
      </Typography>
    </Grid2>
  );
};

const OocyteRetrievalDetails: React.FC<OocyteRetrievalDetailsProps> = ({ cycleId,dropDownOptions }) => {
  const { showLoader, hideLoader } = useLoader();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [retrievalData, setRetrievalData] = useState<any>(null);

  useEffect(() => {
    if (!cycleId) return;

    showLoader();
    getOocyteRetrieval(cycleId)
      .then(data => setRetrievalData(data))
      .catch(() => {
        setRetrievalData(defaultRetrievalData);
      })
      .finally(() => hideLoader());
  }, [cycleId, showLoader, hideLoader]);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Grid2 container spacing={4} mb={3}>
          {renderField('Cycle Start Date', retrievalData?.cycle_start_date)}
          {renderField('OPU Date', retrievalData?.opu_date)}
          {renderField('Start Time', retrievalData?.start_time)}
          {renderField('End Time', retrievalData?.end_time)}
          {renderField('Embryologist', retrievalData?.embryologist_name)}
          {renderField('Clinician Name', retrievalData?.clinician_name)}
          {renderField('Witness Embryologist', retrievalData?.witness_embryologist_name)}
          {renderField('Anesthetist', retrievalData?.anesthetist_name)}
          {renderField(
            'Type of Anesthesia',
            labelFromEnum(dropDownOptions?.type_of_anesthesia ?? [], retrievalData?.type_of_anesthesia)
          )}
          {renderField('Number of Needles Used', retrievalData?.no_of_needles_used)}
          {renderField('Nurse', retrievalData?.nurse_name)}
          {renderField('Tube ID', retrievalData?.tube_id)}
          {renderField(
            'Type of Needle',
            labelFromEnum(dropDownOptions?.type_of_needle ?? [], retrievalData?.type_of_needle)
          )}
          {renderField(
            'Subtype of Needle',
            labelFromEnum(dropDownOptions?.sub_type_of_needle ?? [], retrievalData?.sub_type_of_needle)
          )}
          {renderField(
            'Level of Difficulty',
            labelFromEnum(dropDownOptions?.difficulty_level ?? [], retrievalData?.difficulty_level)
          )}
          {renderField('Procedure Findings', retrievalData?.procedure_findings)}
          {renderField('Optimum Follicles Left', retrievalData?.optimum_follicles?.left_follicles)}
          {renderField('Optimum Follicles Right', retrievalData?.optimum_follicles?.right_follicles)}
          {renderField(
            'Oocyte Retrieved (Left Ovary)',
            retrievalData?.oocytes_retrieved_left
          )}
          {renderField(
            'Oocyte Retrieved (Right Ovary)',
            retrievalData?.oocytes_retrieved_right
          )}
          {renderField('Notes', retrievalData?.notes)}

          <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={retrievalData?.pre_anesthesia_checkup === true}
                  icon={<span style={{ color: 'red', fontSize: '1rem' }}>✖</span>}
                  checkedIcon={<span style={{ color: 'green', fontSize: '1rem' }}>✔</span>}
                  readOnly
                />
              }
              label="Pre-Anesthesia Check-up"
            />
          </Grid2>
        </Grid2>
      </Box>
    </ThemeProvider>
  );
};

export default OocyteRetrievalDetails;

